"use client";
function getMediaUrl(filename) {
  if (!filename) return '';
  // Always use Cloudflare R2 for media
  return `https://pub-0705ed3bb3a5425799ba64b1bd76a81b.r2.dev/shared_media/${encodeURI(filename)}`;
}
import Footer from "@/components/ar-IQ/Footer";
import Navbar from "@/components/ar-IQ/Navbar";
import { useLanguage } from "@/contexts/LanguageContext";
import { getContact } from "@/lib/api";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { useEffect, useRef, useState } from "react";

const ContactPage = () => {
  return (
    <div className="min-h-screen bg-white" dir="rtl">
      <Navbar />
      <HeroSection />
      <ContactInfoSection />
      <ContactFormSection />
      <MapSection />
      <Footer />
    </div>
  );
};

const HeroSection = () => {
  const sectionRef = useRef(null);
  const heroRef = useRef(null);
  const videoRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0.2]);
  const [floatingParticles, setFloatingParticles] = useState([]);
  const [heroData, setHeroData] = useState({
    title: "تواصل معنا!",
    titleHighlight: "",
    subtitle:
      "يسعدنا أن نسمع منك ولنبدأ شيئًا مميزًا معًا\nاتصل بنا لأي استفسار",
    video: { url: getMediaUrl("hero.mp4") },
  });
  const [loading, setLoading] = useState(true);
  const { lang } = useLanguage();

  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 1.2]);

  useEffect(() => {
    const particles = Array.from({ length: 20 }, (_, i) => ({
      id: i,
      top: Math.random() * 100,
      left: Math.random() * 100,
      delay: Math.random() * 3,
      duration: 6 + Math.random() * 4,
    }));
    setFloatingParticles(particles);

    // Fetch hero data
    const fetchHeroData = async () => {
      try {
        setLoading(true);
        const data = await getContact(lang);
        if (data?.hero) {
          setHeroData((prev) => ({
            title: data.hero.title || prev.title || "تواصل معنا!",
            titleHighlight: data.hero.titleHighlight ?? prev.titleHighlight ?? "",
            subtitle:
              data.hero.subtitle ?? prev.subtitle ??
              "يسعدنا أن نسمع منك ولنبدأ شيئًا مميزًا معًا\nاتصل بنا لأي استفسار",
            video: data.hero.video ?? prev.video,
          }));
        }
      } catch (error) {
        console.error("Failed to fetch hero data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchHeroData();
  }, [lang]);

  return (
    <motion.section
      ref={sectionRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{ y: heroY, opacity: heroOpacity }}
    >
      {/* Background Video */}
      <div className="absolute inset-0 will-change-transform">
        {heroData.video?.url ? (
          <div className="absolute inset-0">
            <video
              ref={videoRef}
              autoPlay
              muted
              loop
              playsInline
              preload="none"
              className="absolute inset-0 w-full h-full object-cover"
            >
              <source src={heroData.video.url} type="video/mp4" />
            </video>
            <div className="absolute inset-0 bg-gradient-to-br from-[#0a367b]/90 via-[#0a367b]/70 to-[#0a367b]/50 z-10"></div>
          </div>
        ) : (
          <div
            className="w-full h-full bg-cover bg-center"
            style={{ backgroundImage: `url('${getMediaUrl("arabic.jpg")}')` }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-[#0a367b]/90 via-[#0a367b]/70 to-[#0a367b]/50 z-10"></div>
          </div>
        )}
        {!heroData.video?.url && (
          <div className="absolute inset-0 bg-gradient-to-br from-[#0a367b]/90 via-[#0a367b]/70 to-[#0a367b]/50 z-10"></div>
        )}
      </div>

      {/* Floating Particles */}
      <div className="absolute inset-0">
        {floatingParticles.map((particle) => (
          <motion.div
            key={particle.id}
            className="absolute w-2 h-2 bg-[#dfa428]/40 rounded-full"
            style={{
              top: `${particle.top}%`,
              left: `${particle.left}%`,
            }}
            animate={{
              y: [0, -100, 0],
              opacity: [0, 1, 0],
              scale: [0, 1.5, 0],
            }}
            transition={{
              duration: particle.duration,
              repeat: Infinity,
              delay: particle.delay,
            }}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-6 text-center text-white">
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.5 }}
          className="max-w-5xl mx-auto"
        >

          <motion.h1
            className="text-5xl md:text-7xl font-bold mb-8 leading-tight"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 1 }}
          >
            {heroData.title?.ar || heroData.title}
            {heroData.titleHighlight?.ar && (
              <>
                <br />
                <span className="text-[#dfa428]">{heroData.titleHighlight.ar}</span>
              </>
            )}
          </motion.h1>

          <motion.p
            className="text-xl md:text-2xl leading-relaxed opacity-90 mb-12 whitespace-pre-line"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.5 }}
          >
            {heroData.subtitle?.ar || heroData.subtitle}
          </motion.p>
        </motion.div>
      </div>

      {/* Geometric Shapes */}
      <motion.div
        className="absolute top-20 right-20 w-32 h-32 bg-[#dfa428]/20 rounded-full"
        animate={{
          scale: [1, 1.3, 1],
          rotate: [0, 180, 360],
        }}
        transition={{ duration: 12, repeat: Infinity }}
      />
      <motion.div
        className="absolute bottom-20 left-20 w-24 h-24 bg-white/10 rounded-full"
        animate={{
          scale: [1.2, 1, 1.2],
          rotate: [360, 180, 0],
        }}
        transition={{ duration: 8, repeat: Infinity }}
      />
    </motion.section>
  );
};

const ContactInfoSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });
  const [contactInfoData, setContactInfoData] = useState({
    title: "معلومات التواصل",
    subtitle:
      "تحتاج مساعدة لعمل حملة اعلانية مناسبة لك؟ نحن هنا لمساعدتك في تحقيق أهدافك التسويقية",
    methods: [
      {
        icon: "📞",
        label: "اتصل بنا",
        content: "0780-0800-900",
        color: "from-blue-500 to-blue-700",
        bgColor: "bg-blue-50",
      },
      {
        icon: "✉️",
        label: "البريد الإلكتروني",
        content: "info@palmofbabylon.com",
        color: "from-green-500 to-green-700",
        bgColor: "bg-green-50",
      },
      {
        icon: "📍",
        label: "العنوان",
        content: "العراق، بغداد، الجادرية",
        color: "from-purple-500 to-purple-700",
        bgColor: "bg-purple-50",
      },
    ],
  });
  const { lang } = useLanguage();

  // Helper to extract localized value
  const getLocalizedValue = (value, fallback = "") => {
    if (!value) return fallback;
    if (typeof value === "string") return value;
    if (typeof value === "object" && value !== null) {
      return value.ar || value.en || fallback;
    }
    return fallback;
  };

  useEffect(() => {
    const fetchContactInfo = async () => {
      try {
        const data = await getContact(lang);
        if (data) {
          setContactInfoData({
            title: getLocalizedValue(
              data.contactInfo?.title,
              "معلومات التواصل"
            ),
            subtitle: getLocalizedValue(
              data.contactInfo?.subtitle,
              "تحتاج مساعدة لعمل حملة اعلانية مناسبة لك؟ نحن هنا لمساعدتك في تحقيق أهدافك التسويقية"
            ),
            methods:
              data.contactMethods?.map((method, index) => {
                const defaultMethods = [
                  {
                    icon: "📞",
                    color: "from-blue-500 to-blue-700",
                    bgColor: "bg-blue-50",
                  },
                  {
                    icon: "✉️",
                    color: "from-green-500 to-green-700",
                    bgColor: "bg-green-50",
                  },
                  {
                    icon: "📍",
                    color: "from-purple-500 to-purple-700",
                    bgColor: "bg-purple-50",
                  },
                ];
                return {
                  icon:
                    typeof method.icon === "string"
                      ? method.icon
                      : method.icon?.url || defaultMethods[index]?.icon || "📌",
                  label: getLocalizedValue(method.label, ""),
                  content: getLocalizedValue(method.content, ""),
                  color:
                    defaultMethods[index]?.color || "from-gray-500 to-gray-700",
                  bgColor: defaultMethods[index]?.bgColor || "bg-gray-50",
                  link: method.link || "",
                };
              }) || contactInfoData.methods,
          });
        }
      } catch (error) {
        console.error("Failed to fetch contact info:", error);
      }
    };

    fetchContactInfo();
  }, [lang]);

  return (
    <section
      ref={sectionRef}
      className="py-20 bg-gray-50 relative overflow-hidden"
    >
      {/* Background Elements */}
      <div className="absolute inset-0">
        <motion.div
          className="absolute top-20 right-20 w-64 h-64 bg-gradient-to-br from-[#dfa428]/10 to-[#0a367b]/10 rounded-full"
          animate={{
            rotate: [0, 360],
            scale: [1, 1.2, 1],
          }}
          transition={{ duration: 20, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-20 left-20 w-48 h-48 bg-gradient-to-tr from-[#0a367b]/10 to-[#dfa428]/10 rounded-full"
          animate={{
            rotate: [360, 0],
            scale: [1.2, 1, 1.2],
          }}
          transition={{ duration: 15, repeat: Infinity }}
        />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-[#0a367b] mb-6">
            {contactInfoData.title}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {contactInfoData.subtitle}
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {(() => {
            const mapMethod = contactInfoData.methods.find((m) =>
              (m.icon && m.icon.includes("📍")) ||
              (typeof m.label === "string" && /العنوان|الموقع|site|website|website/i.test(m.label))
            );

            return contactInfoData.methods.map((info, index) => {
              let href = info.link || "";
              const labelStr = (info.label || "").toString();
              const DIRECTIONS_URL =
                "https://maps.google.com/maps/dir//Palm+Of+Babylon+360%C2%B0+Marketing+Company+-+POB+(%D9%86%D8%AE%D9%84%D8%A9+%D8%A8%D8%A7%D8%A8%D9%84+%D9%84%D9%84%D8%AF%D8%B9%D8%A7%D9%8A%D8%A9+%D9%88%D8%A7%D9%84%D8%A7%D8%B9%D9%84%D8%A7%D9%86)+Central+Bank+of+Iraq+Street+Baghdad,+Baghdad+Governorate+10070%E2%80%AD/@33.287331,44.3830371,16z/data=!4m5!4m4!1m0!1m2!1m1!1s0x15577ff6a10b7fbb:0x9be010e3e1401863";

              if ((info.icon && info.icon.includes("📍")) || /العنوان|الموقع/.test(labelStr)) {
                href = DIRECTIONS_URL;
              } else if ((info.icon && info.icon.includes("🔗")) || /الموقع|website|site/.test(labelStr)) {
                href = mapMethod?.link || href;
              }
            const ContactCard = (
              <motion.div
                className={`${
                  info.bgColor
                } rounded-3xl p-8 text-center relative overflow-hidden group hover:shadow-2xl transition-all duration-500 border border-gray-100 ${
                  info.link ? "cursor-pointer" : ""
                }`}
                whileHover={{ scale: 1.05, y: -10 }}
              >
                {/* Gradient Background on Hover */}
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${info.color} opacity-0 group-hover:opacity-90 transition-opacity duration-500 rounded-3xl`}
                ></div>

                {/* Content */}
                <div className="relative z-10 group-hover:text-white transition-colors duration-500">
                  {/* Icon with Animation */}
                  <motion.div
                    className="text-6xl mb-6 inline-block"
                    animate={{
                      rotate: [0, 10, -10, 0],
                      scale: [1, 1.1, 1],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      delay: index * 0.5,
                    }}
                  >
                    {info.icon?.startsWith("http") ? (
                      <img
                        src={info.icon}
                        alt={info.label}
                        className="w-16 h-16 object-contain"
                      />
                    ) : (
                      info.icon
                    )}
                  </motion.div>

                  <h3 className="text-2xl font-bold text-[#0a367b] group-hover:text-white mb-4 transition-colors duration-500">
                    {info.label}
                  </h3>

                  <p className="text-gray-700 group-hover:text-white/90 leading-relaxed text-lg transition-colors duration-500">
                    {(info.link && info.link.startsWith("tel:")) || (info.icon && info.icon.includes("📞")) ? (
                      <span dir="ltr" className="inline-block">
                        {info.content}
                      </span>
                    ) : (
                      info.content
                    )}
                  </p>
                </div>

                {/* Floating Elements */}
                <motion.div
                  className="absolute top-4 right-4 w-4 h-4 bg-[#dfa428]/60 rounded-full"
                  animate={{
                    scale: [1, 1.5, 1],
                    opacity: [0.6, 1, 0.6],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    delay: index * 0.3,
                  }}
                />

                <motion.div
                  className="absolute bottom-4 left-4 w-3 h-3 bg-[#0a367b]/40 rounded-full"
                  animate={{
                    scale: [1.2, 1, 1.2],
                    opacity: [0.4, 0.8, 0.4],
                    y: [0, -5, 0],
                  }}
                  transition={{
                    duration: 2.5,
                    repeat: Infinity,
                    delay: index * 0.4,
                  }}
                />
              </motion.div>
            );

              return (
                <motion.div
                  key={index}
                  className="relative"
                  initial={{ opacity: 0, y: 100 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                >
                  {href ? (
                    <a href={href} target="_blank" rel="noopener noreferrer" className="block">
                      {ContactCard}
                    </a>
                  ) : (
                    ContactCard
                  )}
                </motion.div>
              );
            });
          })()}
        </div>
      </div>
    </section>
  );
};

const ContactFormSection = () => {
  const sectionRef = useRef(null);
  const [particles, setParticles] = useState([]);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });

  const backgroundY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-10%"]);

  const isInView = useInView(sectionRef, { once: true });

  useEffect(() => {
    const generatedParticles = Array.from({ length: 15 }, (_, i) => ({
      id: i,
      top: Math.random() * 100,
      left: Math.random() * 100,
      delay: Math.random() * 2,
      duration: 4 + Math.random() * 2,
    }));
    setParticles(generatedParticles);
  }, []);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Show success popup
    setShowSuccessPopup(true);
    // Reset form
    setFormData({
      fullName: "",
      email: "",
      phone: "",
      subject: "",
      message: "",
    });
    // Hide popup after 3 seconds
    setTimeout(() => {
      setShowSuccessPopup(false);
    }, 3000);
  };

  return (
    <section
      ref={sectionRef}
      className="relative py-32 overflow-hidden"
      style={{
        background: "linear-gradient(135deg, #0C0927 0%, #233C78 100%)",
      }}
    >
      {/* Success Popup */}
      {showSuccessPopup && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="bg-white rounded-3xl p-8 text-center shadow-2xl max-w-md mx-4"
            initial={{ scale: 0.5, y: 50 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.5, y: 50 }}
          >
            <motion.div
              className="text-6xl mb-4"
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 0.5, repeat: 2 }}
            >
              ✅
            </motion.div>
            <h3 className="text-2xl font-bold text-[#0a367b] mb-2">
              تم الإرسال بنجاح!
            </h3>
            <p className="text-gray-600">
              شكراً لك، سنتواصل معك في أقرب وقت ممكن
            </p>
          </motion.div>
        </motion.div>
      )}

      {/* Animated Background */}
      <motion.div className="absolute inset-0" style={{ y: backgroundY }}>
        {/* Floating Particles */}
        {particles.map((particle) => (
          <motion.div
            key={particle.id}
            className="absolute w-3 h-3 bg-[#dfa428]/30 rounded-full"
            style={{
              top: `${particle.top}%`,
              left: `${particle.left}%`,
            }}
            animate={{
              y: [0, -80, 0],
              opacity: [0, 1, 0],
              scale: [0, 1.2, 0],
            }}
            transition={{
              duration: particle.duration,
              repeat: Infinity,
              delay: particle.delay,
            }}
          />
        ))}

        {/* Background Shapes */}
        <motion.div
          className="absolute top-20 right-20 w-48 h-48 bg-[#dfa428]/10 rounded-full"
          animate={{
            scale: [1, 1.3, 1],
            rotate: [0, 180, 360],
          }}
          transition={{ duration: 15, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-20 left-20 w-36 h-36 bg-white/5 rounded-full"
          animate={{
            scale: [1.2, 1, 1.2],
            rotate: [360, 180, 0],
          }}
          transition={{ duration: 12, repeat: Infinity }}
        />
      </motion.div>

      {/* Content */}
      <motion.div
        className="relative z-10 container mx-auto px-6"
        style={{ y: contentY }}
      >
        <div className="max-w-4xl mx-auto">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 50 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-5xl font-bold text-white mb-6">
              أرسل لنا رسالة
            </h2>
            <p className="text-xl text-white/90">
              املأ النموذج أدناه وسنتواصل معك في أقرب وقت ممكن
            </p>
          </motion.div>

          <motion.form
            onSubmit={handleSubmit}
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 md:p-12 shadow-2xl"
            initial={{ opacity: 0, y: 100 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1, delay: 0.3 }}
          >
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.5 }}
              >
                <label className="block text-white font-semibold mb-2">
                  اسمك الكامل *
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-xl bg-white/20 border border-white/30 text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-[#dfa428] transition-all duration-300"
                  placeholder="أدخل اسمك الكامل"
                  required
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.6 }}
              >
                <label className="block text-white font-semibold mb-2">
                  عنوان بريدك الالكتروني *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-xl bg-white/20 border border-white/30 text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-[#dfa428] transition-all duration-300"
                  placeholder="أدخل بريدك الإلكتروني"
                  required
                />
              </motion.div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.7 }}
              >
                <label className="block text-white font-semibold mb-2">
                  رقم هاتفك
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-xl bg-white/20 border border-white/30 text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-[#dfa428] transition-all duration-300 text-right"
                  placeholder="أدخل رقم هاتفك"
                  dir="rtl"
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.8 }}
              >
                <label className="block text-white font-semibold mb-2">
                  عنوان الرسالة *
                </label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-xl bg-white/20 border border-white/30 text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-[#dfa428] transition-all duration-300"
                  placeholder="أدخل عنوان الرسالة"
                  required
                />
              </motion.div>
            </div>

            <motion.div
              className="mb-8"
              initial={{ opacity: 0, y: 50 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.9 }}
            >
              <label className="block text-white font-semibold mb-2">
                نص الرسالة *
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                rows="6"
                className="w-full px-4 py-3 rounded-xl bg-white/20 border border-white/30 text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-[#dfa428] transition-all duration-300 resize-none"
                placeholder="اكتب رسالتك هنا..."
                required
              ></textarea>
            </motion.div>

            <motion.div
              className="text-center"
              initial={{ opacity: 0, y: 50 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 1 }}
            >
              <motion.button
                type="submit"
                className="yellow-button bg-[#dfa428] text-[#0a367b] px-12 py-4 rounded-full text-xl font-bold hover:bg-yellow-500 transition-all duration-300 shadow-lg"
                whileHover={{ scale: 1.05, y: -3 }}
                whileTap={{ scale: 0.95 }}
              >
                ارسل رسالتك
              </motion.button>
            </motion.div>
          </motion.form>
        </div>
      </motion.div>
    </section>
  );
};

const MapSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  return (
    <section
      ref={sectionRef}
      className="py-20 bg-white relative overflow-hidden"
    >
      <div className="container mx-auto px-6">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-[#0a367b] mb-6">
            موقعنا على الخريطة
          </h2>
          <p className="text-xl text-gray-600">
            زورنا في مكتبنا الرئيسي في بغداد
          </p>
        </motion.div>

        <motion.div
          className="relative rounded-3xl overflow-hidden shadow-2xl"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={isInView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 1, delay: 0.3 }}
        >
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3323.730733786054!2d44.38046217594826!3d33.28733548079935!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x15577ff6a10b7fbb%3A0x9be010e3e1401863!2sPalm%20Of%20Babylon%20360%C2%B0%20Marketing%20Company%20-%20POB!5e0!3m2!1sar!2siq!4v1733174400000!5m2!1sar!2siq"
            width="100%"
            height="450"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            className="w-full h-96 md:h-[450px]"
            title="موقع Palm of Babylon على الخريطة"
          ></iframe>
        </motion.div>
      </div>
    </section>
  );
};

export default ContactPage;
